import { useMemo } from 'react';

import { Box, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MonetizationOnOutlined } from '@mui/icons-material';
import { FinanceMonthPeriod, FinancesTotal } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useIsCurrentProviderLive, useIsMobile, useL10n } from '@uplift/hooks';
import { FlexTable, FlexTableColumn, TableCellLoadingWrapper, TableCellMoney } from '@uplift/ui-v2';

import { LinkCell } from './LinkCell';
import { MonthCell } from './MonthCell';

export type FinanceTableRow = FinanceMonthPeriod & {
  isLoading?: boolean;
};

export type FinanceTableProps = {
  rows: FinanceTableRow[];
  totalLoading: boolean;
  total?: FinancesTotal;
};

const TOTALS_ID = 'totals';

const useFinanceTableColumns = () => {
  const l10n = useL10n();
  const isMobile = useIsMobile('sm');
  const me = useCurrentuser();

  const desktop = useMemo(() => {
    const cols = [
      {
        field: 'date',
        headerName: l10n.finance.summaryTable.month,
        renderCell: (params) => <MonthCell {...params} />,
        width: 200,
        sortable: true,
      } as FlexTableColumn<FinanceTableRow, 'date'>,
      {
        field: 'submittedItemsCount',
        headerName: l10n.finance.summaryTable.submittedSessions,
        sortable: false,
        align: 'right',
        minWidth: 120,
        width: me.rules?.msoPayout ? 200 : undefined,
        renderCell: params => (
          <TableCellLoadingWrapper loading={params.row.isLoading} width="30px">
            {params.value}
          </TableCellLoadingWrapper>
        ),
      } as FlexTableColumn<FinanceTableRow, 'submittedItemsCount'>,
      {
        field: 'cashpayItemsCount',
        headerName: l10n.finance.summaryTable.cashPaySessions,
        sortable: false,
        align: 'right',
        minWidth: 120,
        renderCell: params => (
          <TableCellLoadingWrapper loading={params.row.isLoading} width="30px">
            {params.value}
          </TableCellLoadingWrapper>
        ),
      } as FlexTableColumn<FinanceTableRow, 'cashpayItemsCount'>,
      {
        field: 'insuranceItemsCount',
        headerName: l10n.finance.summaryTable.insuranceSessions,
        sortable: false,
        align: 'right',
        minWidth: 120,
        renderCell: params => (
          <TableCellLoadingWrapper loading={params.row.isLoading} width="30px">
            {params.value}
          </TableCellLoadingWrapper>
        ),
      } as FlexTableColumn<FinanceTableRow, 'insuranceItemsCount'>,
      {
        field: 'lateFeeSessionsCount',
        headerName: l10n.finance.summaryTable.cancellationFees,
        sortable: false,
        align: 'right',
        minWidth: 120,
        renderCell: params => (
          <TableCellLoadingWrapper loading={params.row.isLoading} width="30px">
            {params.value}
          </TableCellLoadingWrapper>
        ),
      } as FlexTableColumn<FinanceTableRow, 'lateFeeSessionsCount'>,
      {
        field: 'payout',
        headerName: me.rules?.msoPayout
          ? l10n.finance.summaryTable.billings
          : l10n.finance.summaryTable.payout,
        sortable: false,
        align: 'right',
        minWidth: 120,
        width: me.rules?.msoPayout ? 200 : undefined,
        renderCell: params => (
          <TableCellLoadingWrapper loading={params.row.isLoading} width="60px">
            <TableCellMoney {...params} alignInfoIcons />
          </TableCellLoadingWrapper>
        ),
      } as FlexTableColumn<FinanceTableRow, 'payout'>,
      {
        field: 'id',
        headerName: ' ',
        renderCell: (params) => {
          if (params.row.id === TOTALS_ID) {
            return ' ';
          }

          return <LinkCell {...params} />;
        },
        width: me.rules?.msoPayout ? undefined : 100,
        align: 'right'
      } as FlexTableColumn<FinanceTableRow, 'id'>,
    ];

    if (me.rules?.msoPayout) {
      cols.splice(2, 3);
    }

    return cols;
  }, [ l10n.finance.summaryTable, me.rules?.msoPayout ]);

  const mobile = useMemo(() => {
    const cols = [
      {
        field: 'date',
        headerName: l10n.finance.summaryTable.month,
        renderCell: (params) => <MonthCell {...params} format="MMM YY" />,
        sortable: true,
      } as FlexTableColumn<FinanceTableRow, 'date'>,
      {
        field: 'submittedItemsCount',
        headerName: l10n.finance.summaryTable.sessions,
        align: 'right',
        renderCell: params => (
          <TableCellLoadingWrapper loading={params.row.isLoading} width="30px">
            {params.value}
          </TableCellLoadingWrapper>
        ),
      } as FlexTableColumn<FinanceTableRow, 'submittedItemsCount'>,
      {
        field: 'lateFeeSessionsCount',
        headerName: l10n.finance.summaryTable.fees,
        align: 'right',
        renderCell: params => (
          <TableCellLoadingWrapper loading={params.row.isLoading} width="30px">
            {params.value}
          </TableCellLoadingWrapper>
        ),
      } as FlexTableColumn<FinanceTableRow, 'lateFeeSessionsCount'>,
      {
        field: 'payout',
        headerName: me.rules?.msoPayout
          ? l10n.finance.summaryTable.billings
          : l10n.finance.summaryTable.payout,
        align: 'right',
        renderCell: params => (
          <TableCellLoadingWrapper loading={params.row.isLoading} width="60px">
            <TableCellMoney {...params} />
          </TableCellLoadingWrapper>
        ),
      } as FlexTableColumn<FinanceTableRow, 'payout'>,
      {
        field: 'id',
        headerName: ' ',
        renderCell: (params) => {
          if (params.row.id === TOTALS_ID) {
            return ' ';
          }

          return <LinkCell {...params} />;
        },
      } as FlexTableColumn<FinanceTableRow, 'id'>,
    ];

    if (me.rules?.msoPayout) {
      cols.splice(2, 1);
    }

    return cols;
  }, [ l10n.finance.summaryTable, me.rules?.msoPayout ]);

  return isMobile ? mobile : desktop;
};

const useStyles = makeStyles()(theme => ({
  totalsCell: {
    backgroundColor: theme.palette.secondary.background,
    fontWeight: 700
  }
}));


export const FinanceTable: React.VFC<FinanceTableProps> = ({ rows, total, totalLoading }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const isMobile = useIsMobile('sm');
  const financeTableColumns = useFinanceTableColumns();
  const me = useCurrentuser();
  const isProviderInLiveStatus = useIsCurrentProviderLive(me);
  const totalsRow = useMemo((): FinanceTableRow => {
    return {
      id: TOTALS_ID,
      date: l10n.finance.summaryTable.total,
      hasCalculationsWarning: false,
      payout: total?.payout,
      cashpayItemsCount: total?.cashpayItemsCount,
      insuranceItemsCount: total?.insuranceItemsCount,
      lateFeeSessionsCount: total?.lateFeeSessionsCount,
      submittedItemsCount: total?.submittedItemsCount,
      isLoading: totalLoading,
    };
  }, [ l10n.finance.summaryTable.total, total, totalLoading ]);

  // TODO: Add a second line to the subtitle when a link to the Payment FAQ's is available
  // `Curious about how you’ll get paid? <a href="#" target="_blank" rel="noopener noreferrer">Check out the Payment FAQ's</a>`
  const emptyOverlayContent = !isProviderInLiveStatus && {
    icon: <MonetizationOnOutlined fontSize="inherit" color="action"/>,
    message: 'Finance Dashboard',
    subtitle: 'All financial information about your held sessions will be shown here.'
  };

  const table = (
    <FlexTable
      rows={rows}
      columns={financeTableColumns}
      pinColumn={isMobile ? undefined : [ 'start', 'end' ]}
      pinRow={{
        top: [ totalsRow ],
      }}
      hidePagination
      getCellClassName={(row) => row.id === TOTALS_ID ? classes.totalsCell : undefined}
      cellHeight={isMobile ? 52 : 72}
      headCellHeight={56}
      emptyOverlayProps={emptyOverlayContent}
    />
  );

  if (isMobile) {
    return (
      <Box mx={-2}>
        {table}
      </Box>
    );
  }

  return (
    <Paper elevation={1}>
      {table}
    </Paper>
  );
};
